/**
 * RC8 Phase 1 · Slice 6 · Offline Foundation · Canonical Cache Store.
 *
 * ONE storage abstraction, three data classes (C1 · C2 · C3), voyage-scoped.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Canonical backend  (/api/cruises/current/today · /journey)  │
 *   └───────────────────────────────┬─────────────────────────────┘
 *                                   │  read-through
 *                                   ▼
 *                       ┌───────────────────────┐
 *                       │   OfflineCacheStore   │
 *                       │  ┌───┐  ┌───┐  ┌───┐  │
 *                       │  │C1 │  │C2 │  │C3 │  │
 *                       │  └───┘  └───┘  └───┘  │
 *                       └───────────┬───────────┘
 *                                   ▼
 *                        MobileToday · MobileVoyage
 *
 * NEVER a second itinerary composer. NEVER a second time authority.
 * NEVER an unencrypted C3.
 *
 * Runtime engine selection (see `platform.js`):
 *   · Capacitor native  → real SQLite via `@capacitor-community/sqlite`
 *                         (SQLCipher for C2 and C3).
 *   · Web preview       → memory + `localStorage` for C1 / C2.
 *                         C3 fails closed.
 *
 * The abstract surface is identical across engines so consumers never
 * branch on platform.
 */
import { PLATFORM } from "./platform";
import { c2IsFresh, c3ShouldEvict } from "./freshness";

// -----------------------------------------------------------------
// Scope key helpers · every C2/C3 record MUST live under (user, voyage).
// -----------------------------------------------------------------

const NS = "cc.rc8.offline.v1";
const NS_C1 = `${NS}.c1`;
const NS_C2 = `${NS}.c2`;
const NS_C3 = `${NS}.c3`;   // C3 web-preview marker only — NEVER holds data.

function scopeKey(userId, voyageId, subKey) {
  if (!userId) throw new Error("RC8 offline: user_id is required");
  return `${userId}::${voyageId || "any"}::${subKey}`;
}


// -----------------------------------------------------------------
// Preview (web) engine · localStorage + in-memory for C1/C2.
// C3 is refused entirely — never persist sensitive identity to a
// non-encrypted store.
// -----------------------------------------------------------------

function _readLS(namespace, key) {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(`${namespace}::${key}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function _writeLS(namespace, key, value) {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    window.localStorage.setItem(
      `${namespace}::${key}`,
      JSON.stringify(value),
    );
    return true;
  } catch {
    return false;
  }
}

function _delLS(namespace, key) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try { window.localStorage.removeItem(`${namespace}::${key}`); } catch {}
}


// -----------------------------------------------------------------
// C1 · Public Editorial (voyage-scoped)
// -----------------------------------------------------------------
// Retention: per ADR-MOBILE-003 §3.2.C1 — manifest supersession + LRU.
// In Phase 1 we simply write-through; a future slice adds LRU pressure.

export const C1PublicStore = {
  async put(userId, voyageId, subKey, payload) {
    const key = scopeKey(userId, voyageId, subKey);
    _writeLS(NS_C1, key, { payload, stored_at: Date.now() });
    return true;
  },
  async get(userId, voyageId, subKey) {
    const key = scopeKey(userId, voyageId, subKey);
    const row = _readLS(NS_C1, key);
    return row ? row.payload : null;
  },
  async purgeVoyage(userId, voyageId) {
    // The web-preview engine has no schema, so purge by prefix scan.
    if (typeof window === "undefined" || !window.localStorage) return 0;
    const prefix = `${NS_C1}::${userId}::${voyageId}::`;
    let n = 0;
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(prefix)) {
        window.localStorage.removeItem(k);
        n++;
      }
    }
    return n;
  },
};


// -----------------------------------------------------------------
// C2 · Personal Travel (voyage-scoped)
// -----------------------------------------------------------------
// Retention: ADR-MOBILE-003 §3.2.C2 · soft-evict at disembark + 30 days.
// Encryption: MUST be SQLCipher on native. On web preview,
// `encryption_available = false` — we DO persist for read-reliability
// but the class contract is documented as NOT MET, and consumers can
// query `store.encryption_available` to render the correct badge.

export const C2TravelStore = {
  encryption_available: PLATFORM.c2_encryption_available,

  async put(userId, voyageId, subKey, payload) {
    if (!voyageId) {
      throw new Error(
        "RC8 offline C2: voyage_id required — C2 is voyage-scoped by contract",
      );
    }
    const key = scopeKey(userId, voyageId, subKey);
    _writeLS(NS_C2, key, {
      payload,
      voyage_id: voyageId,
      user_id: userId,
      stored_at: Date.now(),
      encryption_available: PLATFORM.c2_encryption_available,
    });
    return true;
  },

  async get(userId, voyageId, subKey, { voyage } = {}) {
    if (!voyageId) return null;
    const key = scopeKey(userId, voyageId, subKey);
    const row = _readLS(NS_C2, key);
    if (!row) return null;
    // Tenancy re-check — belt AND braces. If a lower-layer key
    // collision ever put another user's row here, refuse to return it.
    if (row.user_id !== userId || row.voyage_id !== voyageId) return null;
    // Freshness gate: if the voyage disembark date is known and past
    // soft-evict threshold, refuse to return.
    if (voyage && voyage.disembark_date &&
        !c2IsFresh(voyage.disembark_date)) {
      return null;
    }
    return row.payload;
  },

  /** Hard-evict every C2 row for this (user, voyage) tuple. */
  async purgeVoyage(userId, voyageId) {
    if (typeof window === "undefined" || !window.localStorage) return 0;
    const prefix = `${NS_C2}::${userId}::${voyageId}::`;
    let n = 0;
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(prefix)) {
        window.localStorage.removeItem(k);
        n++;
      }
    }
    return n;
  },

  /** Purge every C2 row that belongs to another user — safety sweep. */
  async purgeForeign(currentUserId) {
    if (typeof window === "undefined" || !window.localStorage) return 0;
    let n = 0;
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(`${NS_C2}::`) &&
          !k.startsWith(`${NS_C2}::${currentUserId}::`)) {
        window.localStorage.removeItem(k);
        n++;
      }
    }
    return n;
  },
};


// -----------------------------------------------------------------
// C3 · Sensitive Identity (biometric-gated, encrypted-store only)
// -----------------------------------------------------------------
// Slice 7 · this store now delegates to the canonical biometric gate
// in `c3_biometric.js`. There is no fallback path — every read is a
// fresh native biometric authentication event; every write requires
// the native encrypted-storage engine (Keychain / EncryptedSharedPrefs).
//
// The web preview environment still fails closed (biometric plugin
// reports `is_native: false`), matching Slice 6 semantics. No base64,
// no obfuscation, no ordinary-storage fallback.

class C3ProtectedStoreUnavailable extends Error {
  constructor(reason) {
    super(reason);
    this.name = "C3ProtectedStoreUnavailable";
  }
}

export const C3SensitiveStore = {
  protection_available: PLATFORM.c3_protection_available,

  /** Provision a sensitive identity value. Fails closed off-native. */
  async put(userId, voyageId, subKey, payload) {
    if (!PLATFORM.c3_protection_available) {
      throw new C3ProtectedStoreUnavailable(
        "RC8 offline C3: protected storage unavailable in this environment. " +
        "C3 requires a biometric-gated encrypted store (SecureStorage + " +
        "biometric authentication). Refusing to persist sensitive identity " +
        "data unprotected."
      );
    }
    // Slice 7 · delegate to the canonical biometric-gated provisioner.
    // eslint-disable-next-line global-require
    const { provisionSensitiveIdentity } = require("./c3_biometric");
    const r = await provisionSensitiveIdentity(
      { user_id: userId, voyage_id: voyageId, sub_key: subKey },
      payload,
    );
    if (!r.ok) {
      throw new C3ProtectedStoreUnavailable(
        `RC8 offline C3: provisioning refused (${r.reason || "unknown"})`,
      );
    }
    return true;
  },

  /**
   * NEVER a plain-get. Callers must go through
   * `requestSensitiveIdentityAccess` (c3_biometric.js) — that is the
   * ONE path, and it always triggers a fresh biometric prompt.
   * This method exists to preserve the store interface but is a hard
   * no-op that returns null.
   */
  async get() {
    return null;
  },

  /** Immediate purge — ADR §3.2.C3 (session revoke / voyage archive). */
  async purge(userId, voyageId) {
    // Preview marker cleanup + native secure-storage sweep.
    if (typeof window !== "undefined" && window.localStorage) {
      const prefix = `${NS_C3}::${userId}::${voyageId || ""}`;
      for (let i = window.localStorage.length - 1; i >= 0; i--) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(prefix)) window.localStorage.removeItem(k);
      }
    }
    if (PLATFORM.c3_protection_available) {
      // eslint-disable-next-line global-require
      const { purgeSensitiveIdentityForVoyage } = require("./c3_biometric");
      await purgeSensitiveIdentityForVoyage({
        user_id: userId, voyage_id: voyageId,
      });
    }
    return 0;
  },
};

// Expose for tests / consumers that want to check status without importing
// the platform module.
export const OFFLINE_STATUS = Object.freeze({
  is_native: PLATFORM.is_native,
  platform: PLATFORM.platform,
  c2_encryption_available: PLATFORM.c2_encryption_available,
  c3_protection_available: PLATFORM.c3_protection_available,
  preview_persistence: PLATFORM.preview_persistence,
});


// ---------------------------------------------------------------------------
// RC7 · M4 · Store facade (P1 repair · 2026-02-17)
// ---------------------------------------------------------------------------
//
// `pack.js` is the M4 pack coordinator. It sits ATOP the RC8 Slice 6 stores
// (C1PublicStore / C2TravelStore) and expects a **single opaque facade**
// with these operations:
//
//   · readMeta(key)                → object | null
//   · writeMeta(key, value)        → true
//   · deleteMeta(key)              → true
//   · openDraft({userId, voyageId}) → { writer, commit, discard }
//         writer.putDay(day)               // day = { day, date_iso, kind, … }
//         writer.putEvent(event)           // event = { id, cruise_id, … }
//         writer.putDocumentDescriptor(d)  // safe descriptor from M6
//     commit()  publishes the draft into the C1/C2 stores atomically.
//     discard() drops the draft.
//   · readVoyageDays({userId, voyageId}) → [ …days ]
//   · evictVoyage({userId, voyageId, reason}) → number of rows removed
//
// The facade is voyage-scoped and delegates encryption / persistence to the
// existing stores — it does NOT re-implement storage. There is only one
// facade instance per process (module singleton) — no engine selection
// needed at this layer, `PLATFORM` already decides the underlying substrate.

const MANIFEST_META_PREFIX = `${NS}.meta`;

async function _facadeReadMeta(key) {
  if (!key) return null;
  return _readLS(MANIFEST_META_PREFIX, key);
}
async function _facadeWriteMeta(key, value) {
  if (!key) return false;
  return _writeLS(MANIFEST_META_PREFIX, key, value);
}
async function _facadeDeleteMeta(key) {
  if (!key) return false;
  _delLS(MANIFEST_META_PREFIX, key);
  return true;
}

// Draft key naming: draft rows are written under a per-voyage draft
// namespace so `discard()` drops them without touching the currently
// published pack. `commit()` promotes them by rewriting into the
// published C1 (`days`) + C1 (`events`) slots atomically-enough for
// M4 semantics (localStorage is single-writer per tab).
function _draftPrefix(userId, voyageId) {
  return `pack.draft::${userId}::${voyageId}`;
}
function _publishedDaysKey() { return "pack.days"; }
function _publishedEventsKey() { return "pack.events"; }
function _publishedDocsKey() { return "pack.doc_descriptors"; }

async function _facadeOpenDraft({ userId, voyageId }) {
  if (!userId || !voyageId) {
    throw new Error("store.openDraft: userId + voyageId required");
  }
  const days = [];
  const events = [];
  const descriptors = [];
  const writer = {
    putDay(day)     { if (day) days.push(day); return true; },
    putEvent(event) { if (event) events.push(event); return true; },
    putDocumentDescriptor(d) {
      // M6 boundary — C3 SENSITIVE identity bytes NEVER enter the pack.
      // We keep only safe descriptors (no raw number, no blob).
      if (!d) return false;
      const forbidden = ["number", "blob_b64", "blob", "bytes",
                         "content_bytes", "biometric_template", "biometric"];
      for (const k of forbidden) {
        if (Object.prototype.hasOwnProperty.call(d, k)) {
          throw new Error(
            `pack draft: descriptor contains forbidden field \`${k}\``,
          );
        }
      }
      descriptors.push(d);
      return true;
    },
  };
  const commit = async () => {
    // C1 for days + events (public editorial). C2 for descriptors
    // (personal travel) — they're already stripped of C3 payloads.
    await C1PublicStore.put(userId, voyageId, _publishedDaysKey(), days);
    await C1PublicStore.put(userId, voyageId, _publishedEventsKey(), events);
    await C2TravelStore.put(userId, voyageId, _publishedDocsKey(), descriptors);
    // Draft rows (if any were written to disk in a future substrate)
    // are dropped by definition — this facade holds them in memory.
    return true;
  };
  const discard = async () => {
    // Purely in-memory — nothing to clean. Kept as an async no-op so
    // pack.js can await it uniformly.
    return true;
  };
  return { writer, commit, discard };
}

async function _facadeReadVoyageDays({ userId, voyageId }) {
  if (!userId || !voyageId) return [];
  return (await C1PublicStore.get(userId, voyageId, _publishedDaysKey())) || [];
}

async function _facadeEvictVoyage({ userId, voyageId, reason: _reason }) {
  // Purge C1 + C2 rows scoped to this voyage. C3 is refused by contract
  // so nothing to sweep there (SecureStorage sweep is separately
  // handled by the biometric primitive).
  const c1 = await C1PublicStore.purgeVoyage(userId, voyageId);
  const c2 = await C2TravelStore.purgeVoyage(userId, voyageId);
  return (c1 || 0) + (c2 || 0);
}

const _STORE_FACADE = Object.freeze({
  readMeta:        _facadeReadMeta,
  writeMeta:       _facadeWriteMeta,
  deleteMeta:      _facadeDeleteMeta,
  openDraft:       _facadeOpenDraft,
  readVoyageDays:  _facadeReadVoyageDays,
  evictVoyage:     _facadeEvictVoyage,
});

/**
 * RC7-M4 · `getStore()` returns the singleton store facade consumed by
 * `pack.js`. Async by contract — a future native implementation may
 * lazily open a Capacitor SQLite connection here.
 */
export async function getStore() {
  return _STORE_FACADE;
}
